import type { DiretoriaIndicator } from '@/data/diretoriaTypes';

export interface DiretoriaCustoPorMotoModeloAgg {
  label: string;
  marca: string;
  modelo: string;
  quantidade: number;
  custoMedio: number;
}

export interface DiretoriaCustoPorMotoPayload {
  totalCustoAtual: number;
  mediaCustoPorMoto: number;
  motos: Array<{
    placa: string;
    moto: string;
    marca: string;
    statusMoto: string;
    empresa: string;
    custoAtual: number;
  }>;
  porModelo?: DiretoriaCustoPorMotoModeloAgg[];
  querySql?: string;
}

export function aggregateCustoPorModeloClient(
  motos: DiretoriaCustoPorMotoPayload['motos']
): DiretoriaCustoPorMotoModeloAgg[] {
  const map = new Map<string, { marca: string; modelo: string; total: number; count: number }>();
  for (const moto of motos) {
    const marca = moto.marca?.trim() || 'Sem marca';
    const modelo = moto.moto?.trim() || 'Sem modelo';
    const key = `${marca}\0${modelo}`;
    const current = map.get(key) ?? { marca, modelo, total: 0, count: 0 };
    current.total += moto.custoAtual;
    current.count += 1;
    map.set(key, current);
  }
  return Array.from(map.values())
    .map(({ marca, modelo, total, count }) => ({
      label: `${marca} ${modelo}`.trim(),
      marca,
      modelo,
      quantidade: count,
      custoMedio: count ? total / count : 0,
    }))
    .sort((a, b) => b.quantidade - a.quantidade || Math.abs(b.custoMedio) - Math.abs(a.custoMedio));
}

export function buildCustoMotoIndicatorFromErp(
  base: DiretoriaIndicator,
  payload: DiretoriaCustoPorMotoPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const porModelo = payload.porModelo?.length
    ? payload.porModelo
    : aggregateCustoPorModeloClient(payload.motos);
  const chartRows = porModelo.slice(0, 12);

  return {
    ...base,
    shortDescription: 'Custo atual por moto — compra, OS e financeiro (ERP FlyStart).',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Média atual',
        value: formatCurrency(payload.mediaCustoPorMoto),
        hint: 'ERP FlyStart',
        tone: payload.mediaCustoPorMoto < 0 ? 'bad' : 'neutral',
      },
      {
        label: 'Total atual',
        value: formatCurrency(payload.totalCustoAtual),
        hint: 'ERP FlyStart',
        tone: payload.totalCustoAtual < 0 ? 'bad' : 'neutral',
      },
      {
        label: 'Motos (base)',
        value: String(payload.motos.length),
        hint: 'ERP FlyStart',
        tone: 'neutral',
      },
    ],
    chartKind: 'barHorizontal',
    chartTitle: 'Custo médio por marca / modelo',
    series: chartRows.map((row) => ({
      name: row.label.length > 28 ? `${row.label.slice(0, 26)}…` : row.label,
      valor: Math.round(row.custoMedio),
      valor2: row.quantidade,
    })),
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Custo médio', valor2: 'Qtd.' },
    tableHeaders: ['Marca / modelo', 'Custo médio', 'Qtd.'],
    tableRows: porModelo.slice(0, 50).map((row) => [
      row.label,
      formatCurrency(row.custoMedio),
      String(row.quantidade),
    ]),
    footnote:
      'Dados em tempo real do ERP FlyStart (tipo=moto, ativo=1): custo inicial, itens de OS, contas a pagar e contas a receber por equipamento.',
    querySql: payload.querySql,
    dataSource: 'erp',
    hasData: true,
  };
}
